import Constants from "Components/MessageToaster/Constants";
import { CallHistoryMethodAction, push, replace } from "connected-react-router";
import { IChecklistTemplateFilterFactory } from "Factories/Checklists/IChecklistTemplateFilterFactory";
import { IChecklistTemplateController } from "Interfaces/Controllers/IChecklistTemplateController";
import { inject, injectable } from "inversify";
import { DataState } from "Models/Common/DataState";
import { SuggestionFilter } from "Models/Filters/SuggestionFilter";
import { TemplateFilter } from "Models/Filters/TemplateFilter";
import { Dispatch } from "react";
import Types from "ServiceProvider/Types";
import i18n from "Translations/i18n";
import { ChecklistTemplate } from "../../Models/Checklists/ChecklistTemplate";
import { ActionTypes as MessageActionTypes, IShowMessageToasterAction } from "../MessageToaster/MessageToasterActionTypes";
import { ActionTypes, ChecklistTemplateActions } from "./ChecklistTemplateActionTypes";
import { ChecklistTemplateFilterState } from "./ChecklistTemplateFilterState";

export interface IChecklistTemplateAction {
    clearValidationErrorsByIdAsync(checklistTemplateId?: number): any;
    getChecklistTemplateSuggestionsAsync(filter: SuggestionFilter): any;
    getOneChecklistTemplateSuggestionAsync(checklistTemplateId: number): any;
    getChecklistTemplatesByFilterAsync(filter: TemplateFilter, shouldClearCurrentResult?: boolean): any;
    getChecklistTemplateByIdAsync(checklistTemplateId: number): any;
    saveChecklistTemplateAsync(checklistTemplate: ChecklistTemplate, pictureFile?: File, addAnother?: boolean): any;
    duplicateChecklistTemplateAsync(taskTemplate: ChecklistTemplate): any;
    deleteChecklistTemplateByIdAsync(checklistTemplateId: number): any;
}

@injectable()
export class ChecklistTemplateAction implements IChecklistTemplateAction {
    private checklistTemplateController: IChecklistTemplateController;
    private checklistTemplateFilterState: IChecklistTemplateFilterFactory;

    public constructor(
        @inject(Types.IChecklistTemplateController) checklistTemplateController: IChecklistTemplateController,
        @inject(Types.IChecklistTemplateFilterFactory) checklistTemplateFilterState: IChecklistTemplateFilterFactory) {
        this.checklistTemplateController = checklistTemplateController;
        this.checklistTemplateFilterState = checklistTemplateFilterState;
    }

    public getChecklistTemplateSuggestionsAsync(filter: SuggestionFilter) {
        return async (dispatch: Dispatch<ChecklistTemplateActions | IShowMessageToasterAction>) => {

            try {
                const checklistSuggestions = await this.checklistTemplateController.getSuggestionsByFilterAsync(filter);

                dispatch({
                    type: ActionTypes.CHECKLIST_SUGGESTIONS_RETRIEVED,
                    data: checklistSuggestions
                });
            }
            catch (ex) {
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_DANGER, text: i18n.t("checklists.errors.retrieving") }
                });
            }
        };
    }

    public getChecklistTemplatesByFilterAsync(filterState: ChecklistTemplateFilterState, shouldClearCurrentResult: boolean = false) {
        return async (dispatch: Dispatch<ChecklistTemplateActions | IShowMessageToasterAction>) => {
            if (shouldClearCurrentResult) {
                dispatch({
                    type: ActionTypes.CLEAR_CHECKLIST_TEMPLATES
                });
            }

            dispatch({
                type: ActionTypes.CHECKLIST_FILTER_UPDATED,
                filter: filterState
            });

            try {
                const filter = this.checklistTemplateFilterState.create(filterState);
                const checklistTemplatePage = await this.checklistTemplateController.getByFilterAsync(filter);

                dispatch({
                    type: ActionTypes.CHECKLIST_PAGE_RETRIEVED,
                    page: checklistTemplatePage
                });
            }
            catch (ex) {
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_DANGER, text: i18n.t("checklists.errors.retrieving") }
                });
            }
        };
    }

    public getChecklistTemplateByIdAsync(checklistTemplateId: number) {
        return async (dispatch: Dispatch<ChecklistTemplateActions | IShowMessageToasterAction>) => {
            dispatch({
                type: ActionTypes.CHECKLIST_TEMPLATE_STATE_CHANGED,
                checklistTemplateId,
                newState: DataState.Loading
            });

            try {
                const checklistTemplate = await this.checklistTemplateController.getByIdAsync(checklistTemplateId);

                dispatch({
                    type: ActionTypes.CHECKLIST_TEMPLATE_RETRIEVED,
                    checklistTemplate
                });
            }
            catch (ex) {
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_DANGER, text: i18n.t("checklists.errors.retrieving") }
                });
            }
        };
    }

    public getOneChecklistTemplateSuggestionAsync(checklistTemplateId: number) {
        return async (dispatch: Dispatch<ChecklistTemplateActions | IShowMessageToasterAction>) => {
            try {
                const checklistSuggestion = await this.checklistTemplateController.getOneSuggestionAsync(checklistTemplateId);

                dispatch({
                    type: ActionTypes.CHECKLIST_SUGGESTION_RETRIEVED,
                    data: checklistSuggestion
                });
            }
            catch (ex) {
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_DANGER, text: i18n.t("checklists.errors.retrieving") }
                });
            }
        };
    }

    public clearValidationErrorsByIdAsync(checklistTemplateId?: number) {
        return async (dispatch: Dispatch<ChecklistTemplateActions>) => {
            dispatch({
                type: ActionTypes.CLEAR_VALIDATION_ERRORS,
                checklistTemplateId
            });
        };
    }

    public deleteChecklistTemplateByIdAsync(checklistTemplateId: number) {
        return async (dispatch: Dispatch<ChecklistTemplateActions | IShowMessageToasterAction>) => {
            dispatch({
                type: ActionTypes.CHECKLIST_TEMPLATE_STATE_CHANGED,
                checklistTemplateId,
                newState: DataState.Deleting
            });

            try {
                await this.checklistTemplateController.deleteByIdAsync(checklistTemplateId);

                dispatch({
                    type: ActionTypes.CHECKLIST_TEMPLATE_DELETED,
                    checklistTemplateId
                });

                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_SUCCESS, text: i18n.t("checklists.success.deleting") }
                });
            }
            catch (ex) {
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_DANGER, text: i18n.t("checklists.errors.deleting") }
                });
            }
        };
    }

    public saveChecklistTemplateAsync(checklistTemplate: ChecklistTemplate, pictureFile?: File, addAnother?: boolean) {
        return async (dispatch: Dispatch<ChecklistTemplateActions | IShowMessageToasterAction | CallHistoryMethodAction>) => {
            dispatch({
                type: ActionTypes.CHECKLIST_TEMPLATE_STATE_CHANGED,
                checklistTemplateId: checklistTemplate.id,
                newState: DataState.Saving
            });
            dispatch({
                type: ActionTypes.CLEAR_VALIDATION_ERRORS,
                checklistTemplateId: checklistTemplate.id
            });

            try {
                const templateUpdate: ChecklistTemplate = { ...checklistTemplate, area: { ...checklistTemplate.area, parent: undefined, children: undefined } };
                const isNewTemplate = !checklistTemplate.id;

                let newChecklistTemplate = await (isNewTemplate ?
                    this.checklistTemplateController.insertOneAsync(templateUpdate) :
                    this.checklistTemplateController.updateOneAsync(templateUpdate));

                if (pictureFile) {
                    newChecklistTemplate = await this.checklistTemplateController.uploadMediaAsync(newChecklistTemplate, undefined, undefined, pictureFile, undefined);
                }

                for (const item of checklistTemplate.items) {
                    if (item.pictureFile || item.videoFile || item.videoThumbnailFile || item.descriptionFileUrl) {
                        let itemId = item.id;
                        let itemOfNewChecklistTemplate = null;
                        if (!itemId || itemId < 0) {
                            const newItem = newChecklistTemplate.items.find(x => x.positionIndex === item.positionIndex);
                            if (newItem) {
                                itemId = newItem.id;
                                itemOfNewChecklistTemplate = newItem;
                            }
                        }

                        if (itemId) {
                            item.id = itemId;
                            const newSavedItem = await this.checklistTemplateController.uploadItemMediaAsync(newChecklistTemplate, item);
                            if (!itemOfNewChecklistTemplate) {
                                itemOfNewChecklistTemplate = newChecklistTemplate.items.find(x => x.id === item.id);
                            }

                            // Set urls
                            itemOfNewChecklistTemplate!.pictureUrl = newSavedItem.pictureUrl;
                            itemOfNewChecklistTemplate!.videoUrl = newSavedItem.videoUrl;
                            itemOfNewChecklistTemplate!.videoThumbnailUrl = newSavedItem.videoThumbnailUrl;
                            itemOfNewChecklistTemplate!.descriptionFileUrl = newSavedItem.descriptionFileUrl;
                        }
                    }

                    for (const step of item.steps) {
                        if (step.pictureFile || step.videoFile || step.videoThumbnailFile) {

                            const itemOfNewChecklistTemplate = newChecklistTemplate.items.find(x => x.positionIndex === item.positionIndex);
                            let stepId = step.id;

                            let stepOfNewChecklistTemplate = null;
                            if (!stepId || stepId < 0) {
                                const newStep = itemOfNewChecklistTemplate!.steps.find(x => x.positionIndex === step.positionIndex);
                                if (newStep) {
                                    stepId = newStep.id;
                                    stepOfNewChecklistTemplate = newStep;
                                }
                            }

                            if (stepId) {
                                step.id = stepId;
                                const newSavedStep = await this.checklistTemplateController.uploadStepMediaAsync(newChecklistTemplate, itemOfNewChecklistTemplate, step);

                                if (!stepOfNewChecklistTemplate) {
                                    stepOfNewChecklistTemplate = itemOfNewChecklistTemplate!.steps.find(x => x.id === step.id);
                                }

                                // Set urls
                                stepOfNewChecklistTemplate!.pictureUrl = newSavedStep.pictureUrl;
                                stepOfNewChecklistTemplate!.videoUrl = newSavedStep.videoUrl;
                                stepOfNewChecklistTemplate!.videoThumbnailUrl = newSavedStep.videoThumbnailUrl;
                            }
                        }
                    }
                }

                dispatch({
                    type: ActionTypes.CHECKLIST_TEMPLATE_SAVED,
                    checklistTemplate: newChecklistTemplate
                });
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_SUCCESS, text: i18n.t("checklists.success.saved") }
                });

                if (isNewTemplate) {
                    if (!addAnother) {
                        dispatch(push(`${newChecklistTemplate.id}`));
                    }

                    // Set state of new ChecklistsTemplate to done so it wont be saving immediately when adding a new ChecklistsTemplate
                    dispatch({
                        type: ActionTypes.CHECKLIST_TEMPLATE_STATE_CHANGED,
                        newState: DataState.Done
                    });
                }

                if (addAnother) {
                    dispatch(push("/"));
                    dispatch(replace("/checklisttemplates/new"));
                }
            }
            catch (ex) {
                if (ex.validationErrors) {
                    dispatch({
                        type: ActionTypes.SHOW_VALIDATION_ERRORS,
                        validationErrors: ex.validationErrors,
                        checklistTemplateId: checklistTemplate.id
                    });
                }
                else {
                    dispatch({
                        type: MessageActionTypes.SHOW_MESSAGE,
                        data: {
                            type: Constants.MESSAGE_TYPE_DANGER,
                            text: i18n.t(ex.response && ex.response.body ? `checklists.errors.${ex.response.body}` : "checklists.errors.saving")
                        }
                    });
                }

                dispatch({
                    type: ActionTypes.CHECKLIST_TEMPLATE_STATE_CHANGED,
                    checklistTemplateId: checklistTemplate.id,
                    newState: DataState.Done
                });
            }
        };
    }

    public duplicateChecklistTemplateAsync(checklistTemplate: ChecklistTemplate) {
        return async (dispatch: Dispatch<ChecklistTemplateActions | IShowMessageToasterAction | CallHistoryMethodAction>) => {
            dispatch({
                type: ActionTypes.CHECKLIST_TEMPLATE_STATE_CHANGED,
                checklistTemplateId: checklistTemplate.id,
                newState: DataState.Duplicating
            });
            dispatch({
                type: ActionTypes.CLEAR_VALIDATION_ERRORS,
                checklistTemplateId: checklistTemplate.id
            });

            try {
                const newChecklistTemplate = await this.checklistTemplateController.duplicateOneAsync(checklistTemplate);

                dispatch({
                    type: ActionTypes.CHECKLIST_TEMPLATE_SAVED,
                    checklistTemplate: newChecklistTemplate
                });
                dispatch({
                    type: MessageActionTypes.SHOW_MESSAGE,
                    data: { type: Constants.MESSAGE_TYPE_SUCCESS, text: i18n.t("checklists.success.saved") }
                });
                dispatch(push(`${newChecklistTemplate.id}`));
                dispatch({
                    type: ActionTypes.CHECKLIST_TEMPLATE_STATE_CHANGED,
                    checklistTemplateId: checklistTemplate.id,
                    newState: DataState.Done
                });
            }
            catch (ex) {
                if (ex.validationErrors) {
                    dispatch({
                        type: ActionTypes.SHOW_VALIDATION_ERRORS,
                        validationErrors: ex.validationErrors,
                        checklistTemplateId: checklistTemplate.id
                    });
                }
                else {
                    dispatch({
                        type: MessageActionTypes.SHOW_MESSAGE,
                        data: {
                            type: Constants.MESSAGE_TYPE_DANGER,
                            text: i18n.t(ex.response && ex.response.body ? `checklists.errors.${ex.response.body}` : "checklists.errors.saving")
                        }
                    });
                }

                dispatch({
                    type: ActionTypes.CHECKLIST_TEMPLATE_STATE_CHANGED,
                    checklistTemplateId: checklistTemplate.id,
                    newState: DataState.Done
                });
            }
        };
    }
}


// WEBPACK FOOTER //
// ./src/Store/ChecklistTemplates/ChecklistTemplateAction.ts